/* fbp-clamp-message.js
   ============================================================
   Shows a one-time customer message if Checkout Clamp reduced qty.
   Reads:
     - sessionStorage.fbp_clamp_adjusted === "1"
     - sessionStorage.fbp_clamp_details (JSON)
   Then:
     - injects a small notice (Monster cart + theme cart/page)
     - clears the flags so it won’t repeat
   ============================================================
*/
(() => {
  "use strict";

  const DEBUG = true;

  // Where to inject
  const MONSTER_ROOT_SELECTORS = [
    "#mu-items-container",
    ".mu-items-container",
    "#monster-cart",
    ".monster-cart",
    ".monster-cart-drawer",
    "[data-monster-cart]",
  ];

  // Theme cart/page/drawer roots (best effort)
  const THEME_ROOT_SELECTORS = [
    "#CartDrawer",
    "cart-drawer",
    "cart-items",
    "form[action^='/cart']",
    "main",
  ];

  function log(...args) {
    if (!DEBUG) return;
    console.log("[FBP ClampMsg]", ...args);
  }

  function getSS(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  }

  function setSS(key, val) {
    try { sessionStorage.setItem(key, val); } catch {}
  }

  function delSS(key) {
    try { sessionStorage.removeItem(key); } catch {}
  }

  function readDetails() {
    try {
      const raw = getSS("fbp_clamp_details");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function shouldShow() {
    return getSS("fbp_clamp_adjusted") === "1";
  }

  function clearFlags() {
    delSS("fbp_clamp_adjusted");
    // keep details for debugging if you want; comment out next line to retain
    // delSS("fbp_clamp_details");
  }

  function formatMessage(details) {
    const reductions = Array.isArray(details?.reductions) ? details.reductions : [];
    if (!reductions.length) {
      return "We adjusted your cart quantity to match today’s remaining availability.";
    }

    // If multiple lines, keep it simple and generic
    if (reductions.length > 1) {
      return "We adjusted some quantities in your cart to match today’s remaining availability.";
    }

    const r = reductions[0];
    const title = (r?.title || "an item").trim();
    const newQty = Number.isFinite(r?.newQty) ? r.newQty : null;

    if (newQty != null) {
      return `We adjusted “${title}” to quantity ${newQty} to match today’s remaining availability.`;
    }
    return `We adjusted “${title}” to match today’s remaining availability.`;
  }

  function buildNoticeEl(message) {
    const wrap = document.createElement("div");
    wrap.className = "fbp-clamp-notice";
    wrap.setAttribute("role", "status");
    wrap.style.cssText = [
      "position: relative",
      "padding: 12px 14px",
      "margin: 10px 0",
      "border: 1px solid rgba(0,0,0,0.15)",
      "border-radius: 10px",
      "background: rgba(255, 249, 219, 0.9)",
      "color: #111",
      "font-size: 14px",
      "line-height: 1.35",
    ].join(";");

    wrap.innerHTML = `
      <div style="display:flex; gap:10px; align-items:flex-start;">
        <div style="font-weight:700; line-height:1;">Heads up</div>
        <div style="flex:1;">${escapeHtml(message)}</div>
        <button type="button" aria-label="Dismiss message"
          style="border:0;background:transparent;cursor:pointer;font-size:16px;line-height:1;padding:0 2px;">×</button>
      </div>
    `;

    const btn = wrap.querySelector("button");
    btn?.addEventListener("click", () => wrap.remove());

    return wrap;
  }

  function escapeHtml(s) {
    return String(s || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function findFirst(selectors) {
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return null;
  }

  function alreadyInjected(root) {
    return !!root.querySelector?.(".fbp-clamp-notice");
  }

  function injectIntoRoot(root, noticeEl) {
    if (!root || alreadyInjected(root)) return false;

    // Monster: put at top of items container
    // Theme: put near top of root
    root.insertBefore(noticeEl, root.firstChild);
    return true;
  }

  function tryShowOnce() {
    if (!shouldShow()) return false;

    const details = readDetails() || {};
    const message = formatMessage(details);

    const noticeEl = buildNoticeEl(message);

    // Prefer Monster container if present
    const monsterRoot = findFirst(MONSTER_ROOT_SELECTORS);
    if (monsterRoot) {
      const ok = injectIntoRoot(monsterRoot, noticeEl);
      if (ok) {
        log("shown in Monster");
        clearFlags();
        return true;
      }
    }

    // Fall back to theme/cart roots
    const themeRoot = findFirst(THEME_ROOT_SELECTORS);
    if (themeRoot) {
      const ok = injectIntoRoot(themeRoot, noticeEl);
      if (ok) {
        log("shown in theme/cart root");
        clearFlags();
        return true;
      }
    }

    return false;
  }

  // Run now + keep trying briefly as cart UI mounts
  let tries = 0;
  const maxTries = 30;          // ~3s total
  const intervalMs = 100;

  function tick() {
    tries++;
    if (tryShowOnce()) return;
    if (tries >= maxTries) return;
    setTimeout(tick, intervalMs);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", tick);
  } else {
    tick();
  }

  // Also react if Monster cart DOM appears later
  const mo = new MutationObserver(() => {
    // if flags exist, try again
    if (shouldShow()) tryShowOnce();
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Expose for testing
  window.FBP_ClampMsg = {
    showNow: () => tryShowOnce(),
    clear: () => clearFlags(),
  };
})();
